import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../config";
import "./AdminShared.css";

export default function CareerApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("adminInfo") || "null")?.token;

  const fetchApplications = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/careers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setApplications(Array.isArray(data) ? data.reverse() : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) return navigate("/admin");
    fetchApplications();
  }, [token, navigate, fetchApplications]);

  const filtered = useMemo(() => {
    return applications.filter((a) =>
      [a.name, a.email, a.phone, a.jobId?.title]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [applications, search]);

  const approveApplication = async (id) => {
    if (!window.confirm("Approve this candidate?")) return;
    await fetch(`${API_BASE_URL}/api/admin/careers/${id}/approve`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchApplications();
  };

  const rejectApplication = async (id) => {
    if (!window.confirm("Reject this candidate?")) return;
    await fetch(`${API_BASE_URL}/api/admin/careers/${id}/reject`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchApplications();
  };

  const deleteApplication = async (id) => {
    if (!window.confirm("Purge application record?")) return;
    await fetch(`${API_BASE_URL}/api/admin/careers/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchApplications();
    if (selected?._id === id) setSelected(null);
  };

  const exportExcel = () => {
    const data = applications.map((a, i) => ({
      SNo: i + 1,
      Name: a.name,
      Email: a.email,
      Phone: a.phone,
      JobTitle: a.jobId?.title || "-",
      Status: a.status || "Pending",
      AppliedOn: a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Talent Pipeline");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "FlyTutor_Talent_Pipeline.xlsx");
  };

  if (loading) return <div className="admin-list-container"><p style={{padding: '100px', textAlign: 'center'}}>⏳ Scanning Talent Pipeline...</p></div>;

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <button className="admin-back-btn" onClick={() => navigate("/admin/panel")}>←</button>
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Human Resources</h1>
        </div>
        <div className="header-actions-group" style={{ display: 'flex', gap: '10px' }}>
             <button className="module-badge" style={{ cursor: 'pointer', background: '#22c55e', border: 'none' }} onClick={exportExcel}>
                Export Pipeline 📊
            </button>
            <button className="module-badge" style={{ cursor: 'pointer', background: '#3b82f6', border: 'none' }} onClick={fetchApplications}>
                Sync Leads 🔄
            </button>
        </div>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: '#6366f1' }}></div>
            Operator: Recruitment Node
          </div>
          <div className="admin-controls">
                <input
                    type="text"
                    placeholder="Search talent, emails, or job roles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="admin-search-input"
                />
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="elite-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Target Position</th>
                <th>Tenure / Exp</th>
                <th>Work Identity</th>
                <th>Workflow Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                   <td colSpan="6" style={{ textAlign: 'center', padding: '100px', color: '#94a3b8' }}>
                        No records match the active search parameters.
                   </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a._id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{a.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Applied {new Date(a.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{a.jobId?.title || "OPEN INQUIRY"}</span>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{a.jobId?.category || "HR-NODE"}</div>
                    </td>
                    <td>
                        <div style={{ fontWeight: 700, color: '#6366f1' }}>{a.experience || "0"} Years</div>
                    </td>
                    <td>
                        <div style={{ fontSize: '0.85rem' }}>{a.email}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{a.phone}</div>
                    </td>
                    <td>
                        <span className={`status-tag status-${(a.status || 'pending').toLowerCase()}`}>
                            {(a.status || 'PENDING').toUpperCase()}
                        </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="action-btn-elite btn-update" onClick={() => setSelected(a)}><span>👁️</span></button>
                        <button className="action-btn-elite btn-delete" onClick={() => deleteApplication(a._id)}><span>🗑️</span></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="admin-form-overlay" onClick={() => setSelected(null)}>
            <div className="admin-side-panel" style={{ padding: '40px' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h2 style={{ fontWeight: 800, color: '#0f172a', margin: 0 }}>Candidate Dossier</h2>
                    <span className={`status-tag status-${(selected.status || 'pending').toLowerCase()}`}>{selected.status || "PENDING"}</span>
                </div>

                <div className="admin-form-elite side-form" style={{ marginTop: '20px' }}>
                    <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 800, marginBottom: '8px', textTransform: 'uppercase' }}>Full Name</div>
                        <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>{selected.name}</div>
                    </div>

                    <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 800, marginBottom: '8px', textTransform: 'uppercase' }}>Target Profile</div>
                        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#475569' }}>{selected.jobId?.title || "OPEN INQUIRY"}</div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                         <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 800, marginBottom: '6px' }}>TENURE</div>
                            <div style={{ fontWeight: 700, color: '#6366f1' }}>{selected.experience || "0"} YEARS</div>
                        </div>
                        <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 800, marginBottom: '6px' }}>PHONE</div>
                            <div style={{ fontWeight: 700 }}>{selected.phone}</div>
                        </div>
                    </div>
                    
                    {selected.resumeFile && (
                        <a 
                            href={`${API_BASE_URL}/uploads/resumes/${selected.resumeFile}`} 
                            target="_blank" rel="noreferrer"
                            className="btn-primary-elite"
                            style={{ 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', 
                                background: '#0f172a', textDecoration: 'none', transition: 'all 0.3s ease'
                            }}
                        >
                            <span>📄</span> VIEW ATTACHED RESUME
                        </a>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: 'auto' }}>
                    <button className="btn-primary-elite" style={{ flex: 1, background: '#22c55e', padding: '14px' }} onClick={() => approveApplication(selected._id)} disabled={selected.status === 'Approved'}>
                        Approve
                    </button>
                    <button className="btn-primary-elite" style={{ flex: 1, background: '#ef4444', padding: '14px' }} onClick={() => rejectApplication(selected._id)} disabled={selected.status === 'Rejected'}>
                        Reject
                    </button>
                </div>
                <button className="btn-update" style={{ background: '#f1f5f9', border: 'none', width: '100%', padding: '12px', borderRadius: '12px' }} onClick={() => setSelected(null)}>
                    Dismiss Dossier
                </button>
            </div>
        </div>
      )}
    </div>
  );
}
